/* @flow */

import * as React from 'react';
import colorUtils from 'color';
import { isFunction } from 'lodash';
import classNames from 'classnames';
import useSheet from '../../jss';
import FormLabel from '../form/FormLabel';
import InputPrefix from '../form/InputPrefix';

type Props = {
  autoBorderRadius?: boolean,
  autoComplete?: string,
  classes: Object,
  disabled?: boolean,
  helper?: string,
  inputStyle?: Object,
  uppercase?: boolean,
  label?: React.Node,
  lightContent?: boolean,
  max?: number,
  maxLength?: number,
  min?: number,
  name: string,
  noMargin?: boolean,
  onBlur?: (value: string) => void,
  onChange: string => void,
  placeholder?: string,
  prefix?: string,
  required?: boolean,
  size?: 'default' | 'small' | 'large',
  type?: 'email' | 'hidden' | 'number' | 'password' | 'tel' | 'text',
  value: ?number | ?string
};

const defaultProps = {
  autoBorderRadius: true,
  autoComplete: 'autocomplete',
  disabled: false,
  helper: undefined,
  inputStyle: undefined,
  label: undefined,
  lightContent: false,
  max: undefined,
  maxLength: undefined,
  min: undefined,
  noMargin: false,
  onBlur: undefined,
  placeholder: '',
  uppercase: true,
  prefix: undefined,
  required: false,
  size: 'default',
  type: 'text'
};

function InputField({
  autoBorderRadius,
  autoComplete,
  classes,
  disabled,
  helper,
  inputStyle,
  label,
  lightContent,
  name,
  noMargin,
  max,
  maxLength,
  min,
  placeholder,
  prefix,
  onBlur,
  onChange,
  uppercase,
  required,
  size,
  type,
  value
}: Props) {
  return (
    <div className={classNames(classes.field, { 'no-margin': noMargin })}>
      {label && (
        <FormLabel
          htmlFor={name}
          lightContent={lightContent}
          required={required}
          uppercase={uppercase}
        >
          {label}
        </FormLabel>
      )}
      <div className={classes.inputField}>
        <div className={classes.inputFieldInner}>
          {prefix && <InputPrefix>{prefix}</InputPrefix>}
          <input
            id={name}
            type={type}
            name={name}
            className={classNames(classes.input, classes[size], {
              [classes.input_light]: lightContent,
              [classes.input_dark]: !lightContent,
              [classes.input__hasHelper]: helper,
              borderRadius: autoBorderRadius
            })}
            onBlur={event => (isFunction(onBlur) ? onBlur(event.target.value) : undefined)}
            onChange={event => onChange(event.target.value)}
            placeholder={placeholder}
            value={value}
            max={max}
            maxLength={maxLength}
            min={min}
            disabled={disabled}
            autoComplete={autoComplete}
            kind={lightContent ? 'light' : 'dark'}
            style={inputStyle}
          />
        </div>
        {helper && <span className={classes.helper}>{helper}</span>}
      </div>
    </div>
  );
}

InputField.defaultProps = defaultProps;

function applyStyles(theme: Theme) {
  return {
    field: {
      flexGrow: 1,
      flexShrink: 1,
      flexBasis: 'auto',
      '&:not(.no-margin)': {
        marginBottom: theme.spacing.default
      }
    },
    inputField: {
      position: 'relative'
    },
    inputFieldInner: {
      alignItems: 'center',
      display: 'flex'
    },
    default: {
      fontSize: theme.fontSize.large,
      padding: theme.spacing.small
    },
    small: {
      fontSize: theme.fontSize.medium,
      padding: theme.spacing.small
    },
    large: {
      fontSize: theme.fontSize.xlarge,
      padding: theme.spacing.default
    },
    helper: {
      alignItems: 'center',
      display: 'flex',
      color: theme.color.basis4,
      fontSize: theme.fontSize.large,
      fontWeight: theme.fontWeight.demiBold,
      height: '100%',
      left: theme.spacing.small,
      position: 'absolute',
      top: 0
    },
    input_light: {
      backgroundColor: theme.color.basis6,
      color: theme.color.basis1,
      '&::placeholder': {
        color: theme.color.basis4
      },
      '&[disabled]': {
        backgroundColor: colorUtils(theme.color.basis4)
          .alpha(0.5)
          .string()
      },
      '&:focus': {
        backgroundColor: theme.color.basis5
      }
    },
    input_dark: {
      backgroundColor: theme.color.basis2,
      color: theme.color.basis6,
      '&::placeholder': {
        color: theme.color.basis3
      },
      '&[disabled]': {
        backgroundColor: colorUtils(theme.color.basis4)
          .alpha(0.5)
          .string(),
        color: theme.color.basis4
      },
      '&:focus': {
        backgroundColor: theme.color.basis3,
        '&::placeholder': {
          color: theme.color.basis4
        }
      }
    },
    input: {
      border: 'none',
      flexGrow: 1,
      fontWeight: theme.fontWeight.demiBold,
      boxShadow: 'none',
      // outline: 'none',
      paddingLeft: theme.spacing.small,
      transition: 'background-color 0.2s, color 0.2s',
      '&.borderRadius': {
        borderRadius: theme.borderRadius.default
      }
    },
    input__hasHelper: {
      paddingLeft: theme.spacing.small * 3
    }
  };
}

export default useSheet(applyStyles)(InputField);
